import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { X, Loader2, LogIn } from "lucide-react";
import plannerApi, { track } from "@/lib/plannerApi";
import { usePortalAuth } from "@/context/PortalAuthContext";
import { fmtINR } from "@/lib/portalApi";

export const QuoteRequestModal = ({ list, estimate, unresolved, onClose, beforeSubmit }) => {
    const { client } = usePortalAuth();
    const nav = useNavigate();
    const [f, setF] = useState({ delivery_preference: "", notes: "" });
    const [busy, setBusy] = useState(false);
    const next = encodeURIComponent(`/portal/essentials-planner/list/${list.id}`);

    const submit = async () => {
        setBusy(true);
        try {
            await beforeSubmit();
            const r = await plannerApi.post("/quote-requests", { list_id: list.id, ...f });
            track("quote_requested", list.planner_type);
            toast.success("Quotation requested — we'll get back to you soon");
            nav(`/portal/essentials-planner/quotes/${r.data.id}`);
        } catch (e) { toast.error(e.response?.data?.detail || "Couldn't submit"); setBusy(false); }
    };

    return (
        <div className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-4" onClick={onClose} data-testid="planner-quote-modal">
            <div className="w-full sm:max-w-lg bg-white rounded-t-2xl sm:rounded-2xl p-6" onClick={(e) => e.stopPropagation()}>
                <div className="flex items-center justify-between">
                    <div className="text-xl font-black text-slate-900" style={{ fontFamily: "'Chivo',sans-serif" }}>Request HDB quotation</div>
                    <button onClick={onClose} className="text-slate-400" data-testid="planner-quote-modal-close"><X size={18}/></button>
                </div>
                <ul className="mt-4 text-sm text-slate-600 space-y-1.5 list-disc pl-5">
                    <li>We check every item, confirm brands and sizes, and send you a full-basket quotation.</li>
                    <li>Nothing is ordered until you accept the quotation.</li>
                    <li>Planned delivery · pay by UPI or cash on delivery · minimum confirmed order ₹4,000.</li>
                    <li>First complete-list order may get ₹500 off, if eligible — shown in your quotation.</li>
                </ul>
                <div className="mt-4 rounded-xl bg-slate-50 p-3 text-sm flex justify-between" data-testid="planner-quote-estimate">
                    <span>Estimate at current rates{unresolved ? ` (+${unresolved} to confirm)` : ""}</span>
                    <b className="font-mono">{fmtINR(estimate)}</b>
                </div>
                {!client ? (
                    <div className="mt-5" data-testid="planner-quote-signin">
                        <p className="text-sm text-slate-600">Sign in with your mobile number and 4-digit MPIN to send this list. Your draft stays saved.</p>
                        <div className="flex gap-2 mt-3">
                            <Link to={`/portal/signin?next=${next}`} className="flex-1 text-center px-4 py-3 rounded-xl bg-slate-900 text-white font-bold text-sm" data-testid="planner-quote-signin-btn">
                                <LogIn size={14} className="inline mr-1"/> Sign in
                            </Link>
                            <Link to={`/portal/register?next=${next}`} className="flex-1 text-center px-4 py-3 rounded-xl border-2 border-emerald-600 text-emerald-700 font-bold text-sm" data-testid="planner-quote-register-btn">
                                New here? Sign up
                            </Link>
                        </div>
                    </div>
                ) : (
                    <>
                        <label className="block mt-4"><span className="hdb-label">Preferred delivery (day / time)</span>
                            <input className="hdb-input" value={f.delivery_preference} onChange={(e) => setF({ ...f, delivery_preference: e.target.value })}
                                   placeholder="e.g. 1st week of the month, mornings" data-testid="planner-quote-delivery"/></label>
                        <label className="block mt-3"><span className="hdb-label">Anything else?</span>
                            <textarea rows={2} className="hdb-input" value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })} data-testid="planner-quote-notes"/></label>
                        <button onClick={submit} disabled={busy} data-testid="planner-quote-submit"
                                className="mt-5 w-full px-4 py-3.5 rounded-xl bg-emerald-600 text-white font-black hover:bg-emerald-500 inline-flex items-center justify-center gap-2">
                            {busy && <Loader2 size={15} className="animate-spin"/>} Send for quotation
                        </button>
                    </>
                )}
            </div>
        </div>
    );
};
